import React, { useRef, useState } from "react";
import { api, fmtError } from "@/lib/apiClient";
import { Paperclip, X, CheckCircle, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Inline file uploader.
 * Props:
 *  - value: { id, filename, size } | null            (ignored when multiple=true)
 *  - onChange: ({id, filename, size}|null) => void   (ignored when multiple=true)
 *  - multiple?: boolean                              (default false — single-file mode)
 *  - onMultiUploaded?: (files: Array<{id,filename,size,content_type}>) => void
 *  - accept?: string (default any)
 *  - maxSizeMb?: number (default 25)
 *  - testid?: string
 */
export default function FileUploader({ value, onChange, multiple = false, onMultiUploaded, accept, maxSizeMb = 25, testid = "file-uploader" }) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(0);
    const [batch, setBatch] = useState({ done: 0, total: 0 });

    const pick = () => inputRef.current?.click();

    const upload = async (file) => {
        if (file.size > maxSizeMb * 1024 * 1024) {
            toast.error(`File too large (max ${maxSizeMb}MB)`);
            return null;
        }
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/files/upload", form, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (e) => {
                if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
            },
        });
        return { id: data.id, filename: data.filename, size: data.size, content_type: data.content_type };
    };

    const uploadOne = async (file) => {
        setBusy(true);
        setProgress(0);
        try {
            const result = await upload(file);
            if (result) {
                onChange?.(result);
                toast.success("File uploaded");
            }
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
            setProgress(0);
        }
    };

    const uploadMany = async (files) => {
        const list = Array.from(files);
        setBusy(true);
        setBatch({ done: 0, total: list.length });
        const results = [];
        for (let i = 0; i < list.length; i++) {
            try {
                const r = await upload(list[i]);
                if (r) results.push(r);
            } catch (e) {
                toast.error(`${list[i].name}: ${fmtError(e.response?.data?.detail) || e.message}`);
            }
            setBatch({ done: i + 1, total: list.length });
        }
        setBusy(false);
        setProgress(0);
        setBatch({ done: 0, total: 0 });
        if (results.length) {
            toast.success(`Uploaded ${results.length} file${results.length > 1 ? "s" : ""}`);
            onMultiUploaded?.(results);
        }
    };

    const onFileSelect = (e) => {
        const files = e.target.files;
        if (!files || !files.length) return;
        if (multiple) uploadMany(files);
        else uploadOne(files[0]);
        e.target.value = "";
    };

    const onDrop = (e) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (!files || !files.length) return;
        if (multiple) uploadMany(files);
        else uploadOne(files[0]);
    };

    // Single-file "attached" view
    if (!multiple && value) {
        return (
            <div
                className="flex items-center justify-between border border-slate-200 bg-slate-50 px-3 py-2.5"
                data-testid={`${testid}-attached`}
            >
                <div className="flex items-center gap-2.5 min-w-0">
                    <CheckCircle weight="fill" size={16} className="text-green-700 shrink-0" />
                    <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{value.filename}</div>
                        <div className="text-[10px] font-mono text-slate-500">{Math.round((value.size || 0) / 1024)} KB · attached</div>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={pick}
                        className="text-[10px] font-mono uppercase tracking-wider text-slate-600 hover:text-slate-900 px-2 py-1"
                        data-testid={`${testid}-replace`}
                    >
                        <ArrowClockwise size={12} className="inline mr-1" /> Replace
                    </button>
                    <button
                        type="button"
                        onClick={() => onChange?.(null)}
                        className="p-1.5 text-slate-500 hover:text-red-700"
                        data-testid={`${testid}-remove`}
                    >
                        <X size={14} />
                    </button>
                    <input ref={inputRef} type="file" accept={accept} onChange={onFileSelect} className="hidden" />
                </div>
            </div>
        );
    }

    return (
        <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="border border-dashed border-slate-300 bg-white px-4 py-3 flex items-center justify-between gap-3 hover:border-slate-900 transition-colors"
            data-testid={testid}
        >
            <div className="flex items-center gap-3 min-w-0">
                <Paperclip size={16} className="text-slate-400 shrink-0" />
                <div className="min-w-0">
                    <div className="text-sm">
                        {busy
                            ? (multiple && batch.total ? `Uploading ${batch.done}/${batch.total}…` : `Uploading… ${progress}%`)
                            : (multiple ? "Attach files" : "Attach a file")}
                    </div>
                    <div className="text-[10px] font-mono text-slate-500">
                        {busy ? "" : `Drop here or click — ${multiple ? "select multiple, " : ""}up to ${maxSizeMb}MB each`}
                    </div>
                </div>
            </div>
            <button
                type="button"
                onClick={pick}
                disabled={busy}
                className="text-xs font-mono uppercase tracking-wider bg-slate-900 text-white px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50"
                data-testid={`${testid}-choose`}
            >
                {busy ? "…" : "Choose"}
            </button>
            <input ref={inputRef} type="file" accept={accept} multiple={multiple} onChange={onFileSelect} className="hidden" />
        </div>
    );
}
